import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from 'react'

import {type Observable, ObservableMap, ObservableSet, ObservableValue} from '@github-ui/observable'

/**
 * Returns a constant reference to an ObservableValue
 * @param initialValue the initial value of the observable, or a function that
 *                     lazily computes it. The function form is only invoked on
 *                     the first render, so it's preferred when the value is
 *                     expensive to compute (e.g. `useObservableValue(() => derive(x))`).
 */
export function useObservableValue<T>(initialValue: T | (() => T)): ObservableValue<T> {
  const [observableRef] = useState(
    () => new ObservableValue(typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue),
  )
  return observableRef
}

/**
 * Returns a ref to an ObservableValue
 * @param initialValue the initial value of the observable
 */
export function useObservableValueRef<T>(initialValue: T): [RefObject<ObservableValue<T>>, (value: T) => void] {
  const observableRef = useRef<null | ObservableValue<T>>(null)
  if (observableRef.current === null) {
    observableRef.current = new ObservableValue(initialValue)
  }
  const setObservableRef = useCallback((value: T) => {
    if (observableRef.current !== null) {
      observableRef.current.value = value
    }
  }, [])
  return [observableRef as RefObject<ObservableValue<T>>, setObservableRef]
}

/**
 * Returns a constant reference to an ObservableSet
 * @param args the initializer arguments for the underlying Set
 */
export function useObservableSet<T>(...args: ConstructorParameters<typeof Set<T>>): ObservableSet<T> {
  const [observableRef] = useState(() => new ObservableSet(...args))
  return observableRef
}

/**
 * Returns a constant reference to an ObservableMap
 * @param args the initializer arguments for the underlying Map
 */
export function useObservableMap<K, V>(...args: ConstructorParameters<typeof Map<K, V>>) {
  const [observableRef] = useState(() => new ObservableMap(...args))
  return observableRef
}

/** We could use null or undefined, but those are valid values for an observable, so we need a value that is guaranteed unique. */
const NO_VALUE = Symbol('no value')

/**
 * Safely subscribes to and unsubscribes from an observable
 * @param observable the subject observable
 * @param onChange a callback to run in response to changes in the observable
 */
export function useSubscription<T>(observable: Observable<T>, onChange: (value: T) => void): void {
  const initialValueRef = useRef<T | typeof NO_VALUE>(observable.value)
  const handleOnChange = useEffectEvent(onChange)

  useEffect(() => {
    // it is possible that the value has already changed after the initial render and before we make the subscription
    // so we need to call the onChange callback with the current value
    if (initialValueRef.current !== NO_VALUE && initialValueRef.current !== observable.value) {
      handleOnChange(observable.value)
      initialValueRef.current = NO_VALUE
    }
    return observable.subscribe(value => handleOnChange(value))
  }, [observable])
}

/**
 * Returns a stateful value that is updated (re-rendering the component) when the
 * given observable changes. Works with any observable, including `ObservableSet`
 * and `ObservableMap`, which mutate their backing collection in place and notify
 * with the same reference.
 *
 * The snapshot is the observable's monotonic `version` (bumped at mutation time
 * inside `notify`) rather than its value, so:
 * - in-place mutations are detected even though the value reference is stable, and
 * - a mutation that lands between render and subscription still advances the
 *   version, so React's post-subscribe `getSnapshot` read detects it (closing the
 *   render→subscribe race).
 *
 * The live value is returned during render. The value read is keyed on the
 * `version` snapshot (not just `observable`, whose reference is stable) so that
 * the React Compiler cannot memoize and hoist a stale value across re-renders.
 * @param observable the subject observable
 */
export function useObservedState<T>(observable: Observable<T>): T {
  const subscribe = useCallback((onStoreChange: () => void) => observable.subscribe(onStoreChange), [observable])
  const getSnapshot = useCallback(() => observable.version, [observable])
  const version = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return useMemo(() => {
    // we need to read the value during render so that we don't miss any updates that happen between render
    // and the effect subscription, but we key the read on the version so that React's memoization
    // doesn't cause us to return a stale value across renders.
    // The version is guaranteed to advance on any mutation (even in-place mutations of ObservableSet/ObservableMap) because it's bumped inside notify,
    // which runs after every mutation.
    void version
    return observable.value
  }, [observable, version])
}

/**
 * Returns a constant reference to an observable that is updated in
 * response to changes in the given observable.
 * @param observable the subject observable
 * @param derive a function which transforms the value of the subject
 *               observable to the value of the returned observable
 */
export function useDerivedObservable<T, D>(
  observable: ObservableValue<T>,
  derive: (value: T) => D,
): ObservableValue<D> {
  const derivedObservable = useObservableValue(() => derive(observable.value))

  useSubscription(observable, value => {
    derivedObservable.setValue(derive(value))
  })

  return derivedObservable
}
